// web/src/components/PrivacyPolicy.tsx
import React from "react";
import { useNavigate } from "react-router-dom";
import PageLayout from "./PageLayout";
import { Account } from "./TopMenuBar";

interface PrivacyPolicyProps {
  account?: Account | null;
  onLogout?: () => void;
}

export default function PrivacyPolicy({ account, onLogout }: PrivacyPolicyProps) {
  const navigate = useNavigate();

  return (
    <PageLayout account={account} onLogout={onLogout}>
      <div className="max-w-4xl mx-auto px-4 py-8">
        <div className="rounded-2xl shadow-xl border-4 p-6 md:p-8" style={{
          background: 'linear-gradient(to bottom, #f7b267 0%, #f4a261 100%)',
          borderColor: '#8b6542'
        }}>
          <h1 className="text-3xl md:text-4xl font-bold mb-6" style={{ color: '#3e2615' }}>
            Privacy Policy
          </h1>
          
          <div className="text-sm mb-6" style={{ color: '#3e2615', opacity: 0.8 }}>
            <p>Effective Date: 11/18/2025</p>
            <p>Last Updated: 1/4/2026</p>
          </div>

          <div className="space-y-8 text-base md:text-lg" style={{ color: '#3e2615' }}>
            
            {/* Introduction */}
            <section>
              <p className="mb-4">
                Goldrush ("the Game") is developed and operated by Slantwise AI ("we", "us", or "our").
                This Privacy Policy explains what information we collect, how we use it, and the choices available to you.
              </p>
              <p className="mb-3">
                If you have any questions, you may contact us at:
              </p>
              <p className="mb-4">
                📧 <a href="mailto:support@slantwise.ai" className="underline">support@slantwise.ai</a>
              </p>
            </section>

            {/* Section 1: Information We Collect */}
            <section>
              <h2 className="text-2xl font-bold mb-4" style={{ color: '#1a0f08' }}>1. Information We Collect</h2>
              <p className="mb-4">
                We collect only the information necessary to operate the Game, maintain accounts, provide multiplayer functionality, and ensure security.
              </p>
              
              <h3 className="text-xl font-semibold mb-2 mt-4" style={{ color: '#1a0f08' }}>A. Account Information</h3>
              <p className="mb-2">We collect the following when you create an account:</p>
              <ul className="list-disc list-inside ml-4 space-y-1 mb-4">
                <li>Username (required for all players, including guests)</li>
                <li>Email address (registered users only)</li>
                <li>Password (securely hashed; never stored in plain text)</li>
                <li>Account creation date and last login</li>
              </ul>
              <p className="mb-4">
                Guest players provide only a username; optional registration requires email and password.
              </p>

              <h3 className="text-xl font-semibold mb-2 mt-4" style={{ color: '#1a0f08' }}>B. Gameplay Data</h3>
              <p className="mb-2">To operate game features and progression, we store:</p>
              <ul className="list-disc list-inside ml-4 space-y-1 mb-4">
                <li>Match history (wins, losses, ties)</li>
                <li>Game statistics and ratings</li>
                <li>Leaderboard rankings</li>
                <li>Glint balance and Gear balance</li>
                <li>Purchase history of Gear or upgrades</li>
                <li>Tournament participation (if applicable in future updates)</li>
              </ul>

              <h3 className="text-xl font-semibold mb-2 mt-4" style={{ color: '#1a0f08' }}>C. Technical Information</h3>
              <p className="mb-2">Automatically collected for security, performance, and authentication:</p>
              <ul className="list-disc list-inside ml-4 space-y-1 mb-4">
                <li>IP address (anti-fraud, rate limiting, session security)</li>
                <li>Browser type and version</li>
                <li>Device information (high-level, not precise fingerprinting)</li>
                <li>Session cookies</li>
                <li>Local storage / IndexedDB (for guest account persistence)</li>
                <li>Authentication tokens (JWT)</li>
              </ul>
              <p className="mb-4">
                We do not collect precise location, advertising identifiers, or tracking profiles.
              </p>

              <h3 className="text-xl font-semibold mb-2 mt-4" style={{ color: '#1a0f08' }}>D. Payment Information</h3>
              <p className="mb-2">Goldrush uses Stripe to process purchases.</p>
              <ul className="list-disc list-inside ml-4 space-y-1 mb-4">
                <li>We do not store credit card numbers or banking information</li>
                <li>Stripe handles payment card details in accordance with its own privacy policy</li>
                <li>We store purchase records (transaction IDs, amounts) for your account history</li>
              </ul>

              <h3 className="text-xl font-semibold mb-2 mt-4" style={{ color: '#1a0f08' }}>E. Analytics &amp; Error Diagnostics</h3>
              <p className="mb-2">We collect limited, pseudonymous analytics and error diagnostics to understand how the Game is used and to improve stability and performance. This includes:</p>
              <ul className="list-disc list-inside ml-4 space-y-1 mb-4">
                <li>Anonymous usage events (such as game starts, round progression, and feature interactions)</li>
                <li>Application error and crash reports</li>
                <li>High-level device and platform information (e.g., operating system, app version)</li>
              </ul>
              <p className="mb-4">
                This data does not include personal identifiers such as names, email addresses, or advertising identifiers and is not used for advertising or cross-app tracking.
              </p>
            </section>

            {/* Section 2: How We Use Your Information */}
            <section>
              <h2 className="text-2xl font-bold mb-4" style={{ color: '#1a0f08' }}>2. How We Use Your Information</h2>
              <p className="mb-2">We use collected information to:</p>
              <ul className="list-disc list-inside ml-4 space-y-1 mb-4">
                <li>Provide and maintain the Game and its features</li>
                <li>Match players for online multiplayer games</li>
                <li>Operate leaderboards</li>
                <li>Process in-game purchases</li>
                <li>Send account verification emails</li>
                <li>Prevent fraud and abuse</li>
                <li>Improve gameplay balance, performance, and user experience</li>
                <li>Secure accounts and game systems</li>
                <li>Analyze anonymous gameplay, usage patterns, and error reports to improve game stability, performance, and user experience</li>
              </ul>
              <p className="mb-4">
                We do not sell personal information.
              </p>
            </section>

            {/* Section 3: Data Storage & Security */}
            <section>
              <h2 className="text-2xl font-bold mb-4" style={{ color: '#1a0f08' }}>3. Data Storage & Security</h2>
              <p className="mb-2">We take reasonable measures to safeguard your data:</p>
              <ul className="list-disc list-inside ml-4 space-y-1 mb-4">
                <li>Passwords hashed using industry-standard algorithms</li>
                <li>All data transmitted via HTTPS encryption</li>
                <li>Authentication handled using secure tokens (JWT)</li>
                <li>Guest account persistence handled with IndexedDB and server records</li>
                <li>Servers operated by professional hosting providers with appropriate security measures</li>
              </ul>
              <p className="mb-4">
                No method of storage or transmission is 100% secure, but we follow best practices to protect player data.
              </p>
            </section>

            {/* Section 4: Third-Party Services */}
            <section>
              <h2 className="text-2xl font-bold mb-4" style={{ color: '#1a0f08' }}>4. Third-Party Services</h2>
              <p className="mb-2">We use third-party services only where necessary to operate and improve the Game:</p>
              <ul className="list-disc list-inside ml-4 space-y-1 mb-4">
                <li><strong>PostHog (Analytics):</strong> Used to collect anonymous usage analytics, such as gameplay flow and feature interactions, to help us understand how the Game is used and identify areas for improvement.</li>
                <li><strong>Sentry (Error Monitoring):</strong> Used to collect application error reports and crash diagnostics to help identify, debug, and fix software issues.</li>
                <li><strong>Stripe (Payments):</strong> Handles payment processing. We do not store credit card numbers or banking information. Stripe processes payment information in accordance with its own privacy policy.</li>
                <li><strong>Email Provider:</strong> Used to send verification and account recovery emails.</li>
                <li><strong>Hosting Provider:</strong> Used for server infrastructure and data storage.</li>
              </ul>
              <p className="mb-4">
                These service providers process data only on our behalf and under our instructions. We do not share player data with advertisers or sell personal information.
              </p>
            </section>

            {/* Section 5: Cookies & Local Storage */}
            <section>
              <h2 className="text-2xl font-bold mb-4" style={{ color: '#1a0f08' }}>5. Cookies & Local Storage</h2>
              <p className="mb-2">Goldrush uses:</p>
              
              <h3 className="text-xl font-semibold mb-2 mt-4" style={{ color: '#1a0f08' }}>Cookies</h3>
              <ul className="list-disc list-inside ml-4 space-y-1 mb-4">
                <li>Session cookies for authentication</li>
                <li>Security cookies for fraud prevention</li>
              </ul>

              <h3 className="text-xl font-semibold mb-2 mt-4" style={{ color: '#1a0f08' }}>Local Storage / IndexedDB</h3>
              <ul className="list-disc list-inside ml-4 space-y-1 mb-4">
                <li>Guest account persistence (device keys, wrapped private keys, session tokens)</li>
                <li>Username and temporary game state</li>
                <li>Device-based limitations to prevent multiple guest accounts</li>
              </ul>

              <h3 className="text-xl font-semibold mb-2 mt-4" style={{ color: '#1a0f08' }}>SessionStorage</h3>
              <ul className="list-disc list-inside ml-4 space-y-1 mb-4">
                <li>Logout state and session continuity</li>
              </ul>

              <p className="mb-4">
                Disabling cookies or local storage may prevent the Game from functioning properly.
              </p>
            </section>

            {/* Section 6: Children's Privacy */}
            <section>
              <h2 className="text-2xl font-bold mb-4" style={{ color: '#1a0f08' }}>6. Children's Privacy</h2>
              <p className="mb-2">
                Goldrush is not intended for children under the age required by local data protection laws (typically 13 under COPPA and 16 under GDPR where applicable).
              </p>
              <p className="mb-2">
                We do not knowingly collect personal information from children without verifiable parental consent.
              </p>
              <p className="mb-4">
                Parents may request deletion of their child's data by contacting <a href="mailto:support@slantwise.ai" className="underline">support@slantwise.ai</a>.
              </p>
            </section>

            {/* Section 7: User Rights */}
            <section>
              <h2 className="text-2xl font-bold mb-4" style={{ color: '#1a0f08' }}>7. User Rights (GDPR / CCPA)</h2>
              <p className="mb-2">Depending on your region, you may have the right to:</p>
              <ul className="list-disc list-inside ml-4 space-y-1 mb-4">
                <li>Access your stored personal information</li>
                <li>Correct inaccurate information</li>
                <li>Delete your account and associated data</li>
                <li>Request a copy of your data (data portability)</li>
                <li>Object to or restrict certain processing</li>
                <li>Withdraw consent at any time</li>
              </ul>
              <p className="mb-2">
                To exercise these rights, contact: <a href="mailto:support@slantwise.ai" className="underline">support@slantwise.ai</a>
              </p>
              <p className="mb-4">
                We respond to all requests within the required legal timeframe.
              </p>
            </section>

            {/* Section 8: Data Sharing & Disclosure */}
            <section>
              <h2 className="text-2xl font-bold mb-4" style={{ color: '#1a0f08' }}>8. Data Sharing & Disclosure</h2>
              <p className="mb-2">We do not sell or rent personal data.</p>
              <p className="mb-2">Analytics and error monitoring data is shared only with service providers acting on our behalf for the purposes described in this policy.</p>
              <p className="mb-2">We share information only with:</p>
              <ul className="list-disc list-inside ml-4 space-y-1 mb-4">
                <li>Service providers necessary to operate the Game (hosting, email, payments)</li>
                <li>Law enforcement, only when legally required</li>
                <li>Other players, but only username, leaderboard rank, and gameplay performance (never private data)</li>
              </ul>
            </section>

            {/* Section 9: Data Retention */}
            <section>
              <h2 className="text-2xl font-bold mb-4" style={{ color: '#1a0f08' }}>9. Data Retention</h2>
              <p className="mb-2">We retain data for as long as necessary to operate the Game:</p>
              <ul className="list-disc list-inside ml-4 space-y-1 mb-4">
                <li>User account data: retained until deleted by the user</li>
                <li>Gameplay statistics: retained while the account is active</li>
                <li>Guest accounts: retained according to server policies but may be removed after inactivity</li>
                <li>Purchase records: retained as required for financial and legal compliance</li>
              </ul>
              <p className="mb-4">
                You may request deletion at any time.
              </p>
            </section>

            {/* Section 10: International Data Transfers */}
            <section>
              <h2 className="text-2xl font-bold mb-4" style={{ color: '#1a0f08' }}>10. International Data Transfers</h2>
              <p className="mb-2">
                Your data may be stored or processed in countries outside your residence.
              </p>
              <p className="mb-4">
                We use reputable hosting providers with appropriate safeguards for international transfers.
              </p>
            </section>

            {/* Section 11: Changes to This Policy */}
            <section>
              <h2 className="text-2xl font-bold mb-4" style={{ color: '#1a0f08' }}>11. Changes to This Policy</h2>
              <p className="mb-2">
                We may update this Privacy Policy when necessary.
              </p>
              <p className="mb-2">
                If changes are significant, we will notify users through the Game or via email.
              </p>
              <p className="mb-4">
                Continued use of the Game after changes constitutes acceptance of the revised policy.
              </p>
            </section>

            {/* Section 12: Contact Us */}
            <section>
              <h2 className="text-2xl font-bold mb-4" style={{ color: '#1a0f08' }}>12. Contact Us</h2>
              <p className="mb-2">
                For privacy questions, concerns, or data requests, email:
              </p>
              <p className="mb-4">
                📧 <a href="mailto:support@slantwise.ai" className="underline">support@slantwise.ai</a>
              </p>
            </section>

          </div>
        </div>
      </div>
    </PageLayout>
  );
}
